/**
 * User Profile Component
 *
 * Display user profile with logout and account deletion.
 */

import { useState } from 'react'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { toast } from '@/lib/toast'
import { useAuth } from '../hooks/useAuth'
import { userApi } from '../api/userApi'

export const UserProfile = () => {
  const { user, logout, loading } = useAuth()
  const [loggingOut, setLoggingOut] = useState(false)
  // The confirm dialog is controlled instead of using AlertDialogTrigger: the trigger
  // would live inside DropdownMenuItem, and selecting the item closes the menu, which
  // unmounts the trigger and the dialog with it.
  const [confirmingDelete, setConfirmingDelete] = useState(false)
  const [deleting, setDeleting] = useState(false)

  if (!user) {
    return null
  }

  const handleLogout = async () => {
    setLoggingOut(true)
    try {
      await logout()
    } catch (error) {
      console.error('Logout failed:', error)
    } finally {
      setLoggingOut(false)
    }
  }

  const handleDeleteAccount = async () => {
    setDeleting(true)
    try {
      await userApi.deleteAccount()
    } catch (error) {
      console.error('Account deletion failed:', error)
      toast.error('탈퇴하지 못했어요', '잠시 후 다시 시도해 주세요.')
      setDeleting(false)
      return
    }

    toast.success('탈퇴가 완료됐어요', '그동안 이용해 주셔서 고마워요.')
    // The account is already gone on the server, so a failed sign-out must not read as
    // a failed withdrawal. Sign-out drops `user`, the sidebar swaps back to the login
    // button and this component unmounts — no state updates after this point.
    await logout().catch(() => undefined)
  }

  const displayName = user.firebaseUser.displayName || user.firebaseUser.email || 'User'
  const email = user.firebaseUser.email || ''
  const photoURL = user.firebaseUser.photoURL

  // Get initials for avatar fallback
  const initials = displayName
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2)

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" className="relative h-10 w-10 rounded-full">
            <Avatar>
              <AvatarImage src={photoURL || undefined} alt={displayName} />
              <AvatarFallback>{initials}</AvatarFallback>
            </Avatar>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          <DropdownMenuLabel>
            <div className="flex flex-col space-y-1">
              <p className="text-sm font-medium leading-none">{displayName}</p>
              <p className="text-xs leading-none text-muted-foreground">{email}</p>
            </div>
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem disabled={loggingOut || loading} onClick={handleLogout}>
            {loggingOut ? '로그아웃 중...' : '로그아웃'}
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            className="text-destructive focus:text-destructive"
            onSelect={() => setConfirmingDelete(true)}
          >
            회원 탈퇴
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <AlertDialog open={confirmingDelete} onOpenChange={setConfirmingDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>회원 탈퇴</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2">
                <p>탈퇴하면 아래 정보가 바로 지워져요. 되돌릴 수 없어요.</p>
                <ul className="list-disc space-y-1 pl-5">
                  <li>로그인 계정 ({email || '이메일 계정'})</li>
                  <li>저장한 키워드와 등록한 키워드</li>
                  <li>표시 개수·정렬·테마·언어 등 화면 설정</li>
                  <li>AI 요약 크레딧 사용 기록</li>
                  <li>아침 브리핑 수신 설정과 받을 이메일</li>
                </ul>
                <p>
                  공유용으로 만든 뉴스 클리핑 링크는 계정과 따로 보관돼서 탈퇴해도 남아 있고, 만든
                  날부터 90일이 지나면 자동으로 사라져요.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>취소</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={deleting}
              // Radix closes the dialog on action click; the request needs it to stay
              // open so the button can show progress and a failure can be retried.
              onClick={(event) => {
                event.preventDefault()
                void handleDeleteAccount()
              }}
            >
              {deleting ? '탈퇴 중...' : '탈퇴하기'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
